/**
 * Module dependencies.
 */

import { ReactNode } from 'react';
import { Svg } from 'src/components/core/svg';
import { Text } from 'src/components/core/text';
import { ifProp } from 'styled-tools';
import { media } from '@untile/react-core/styles/media';
import { transparentize } from 'src/styles/utils/colors';
import chevronSvg from 'src/assets/svgs/chevron-down.svg';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  ariaLabelledBy?: string;
  children: ReactNode;
  className?: string;
  icon?: string;
  isExpanded?: boolean;
  isLight?: boolean;
  onClick?: () => void;
  title?: string;
};

/**
 * `Heading` styled component.
 */

const Heading = styled.span`
  align-items: center;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  display: grid;
  grid-gap: var(--space-xs);
  grid-template-columns: var(--collaspe-grid-columns);
  outline: none;
  padding: var(--space-xs) 0;
  position: relative;
  text-align: inherit;

  :focus,
  :hover {
    --collapse-chevron-color: var(--color-primary);
  }
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div`
  display: grid;
  grid-template-rows: 0fr;
  overflow: hidden;
  transition: grid-template-rows var(--transition-default);
`;

/**
 * `Content` styled component.
 */

const Content = styled(Text).attrs({ as: 'div', variant: 'bodyL' })`
  font-weight: 400;
  opacity: 0;
  overflow: hidden;
  padding: var(--space-ms) var(--space-sm) var(--space-xs) 0;
  transform: translateY(-100%);
  transition: var(--transition-default);
  transition-property: opacity, transform;

  ${media.min.ms`
    padding: var(--space-sm) var(--space-sm) var(--space-xs) 0;
  `}
`;

/**
 * `IconWrapper` styled component.
 */

const IconWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  position: relative;
`;

/**
 * `Icon` styled component.
 */

const Icon = styled(Svg).attrs({
  size: '28px'
})`
  display: flex;
  height: 28px;
  justify-content: center;
`;

/**
 * `ChevronIcon` styled component.
 */

const ChevronIcon = styled(Svg).attrs({
  icon: chevronSvg,
  size: 'var(--space-sm)'
})`
  color: var(--collapse-chevron-color);
  transition: var(--transition-default);
  transition-property: color, transform;
`;

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.button<{ hasIcon: boolean }>`
  --collapse-active-background: ${transparentize('cyan800', 0.3)};
  --collapse-background: ${transparentize('cyan800', 0.16)};
  --collapse-chevron-color: var(--color-text);
  --collaspe-grid-columns: 1fr var(--space-ms);
  --collaspe-text-color: var(--color-text);

  ${ifProp('hasIcon', `--collaspe-grid-columns: var(--space-ms) 1fr var(--space-ms);`)}

  all: unset;
  appearance: none;
  background: var(--collapse-background);
  border-radius: var(--border-radius-base);
  box-shadow: none;
  color: var(--collaspe-text-color);
  display: grid;
  overflow: hidden;
  padding: 0 var(--space-sm);
  transition: background-color var(--transition-default);
  width: calc(100% - calc(var(--space-sm) * 2));

  :focus,
  :hover {
    --collapse-background: var(--collapse-active-background);
  }

  &[aria-expanded='true'] {
    --collapse-background: var(--collapse-active-background);

    ${ChevronIcon} {
      transform: rotate(-180deg);
    }

    ${Grid} {
      grid-template-rows: 1fr;
    }

    ${Content} {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &[data-theme-light='true'] {
    --collapse-active-background: var(--gradient-primary--15-deg--20);
    --collapse-background: var(--gradient-primary--15-deg--10);
    --collapse-chevron-color: var(--color-slate900);
    --collaspe-text-color: var(--color-text-secondary);
  }

  ${media.min.md`
    padding: 0 var(--space-xs);
  `}

  ${media.min.xl`
    padding: 0 var(--space-sm);
  `}
`;

/**
 * Export `Collapse` styled component.
 */

export function Collapse(props: Props) {
  const { children, className, icon, isExpanded, isLight, onClick, title, ...rest } = props;

  return (
    <Wrapper aria-expanded={isExpanded} className={className} data-theme-light={isLight} hasIcon={!!icon}>
      <Heading onClick={onClick} {...rest}>
        {icon && (
          <IconWrapper>
            <Icon icon={icon} />
          </IconWrapper>
        )}

        <Text block variant={'bodyL'} weight={600}>
          {title}
        </Text>

        <IconWrapper>
          <ChevronIcon />
        </IconWrapper>
      </Heading>

      <Grid>
        <div style={{ minHeight: 0 }}>
          <Content>{children}</Content>
        </div>
      </Grid>
    </Wrapper>
  );
}
